import type { FacetTone } from "./types";

type LabelMap = Record<string, string>;

export type FacetItemType = "risks" | "scope" | "requirements";

export const systemLabels = {
  heating_cooling: "Heating & cooling",
  underfloor_heating: "Underfloor heating",
  whole_home_ui: "Whole-home UI",
  metering: "Energy metering",
  bms_integration: "BMS integration",
  network_infra: "Network & infra",
  commissioning: "Commissioning"
} as const satisfies LabelMap;

export const riskNatureLabels = {
  capacity: "Capacity / fit",
  catalog_gap: "Product gap",
  conflict: "Requirement conflict",
  scope_ambiguity: "Scope ambiguity",
  cost_note: "Cost / VE note",
  clarification: "Needs an answer",
  other: "Other"
} as const satisfies LabelMap;

export const ownerLabels = {
  client: "Client",
  engine: "Auto-resolvable",
  estimator: "Commercial",
  installer: "Installer / electrical",
  user: "Us (estimator)"
} as const satisfies LabelMap;

export const severityLabels = {
  blocker: "Blocker",
  warning: "Warning",
  info: "Info"
} as const satisfies LabelMap;

export const riskStatusLabels = {
  open: "Open",
  accepted: "Accepted",
  resolved: "Resolved",
  converted_to_rfi: "Converted to RFI"
} as const satisfies LabelMap;

export const scopeWorkTypeLabels = {
  commissioning: "Commissioning & test",
  configuration: "Configuration / software",
  documentation: "Documentation / O&M",
  install: "Install / labour",
  integration: "Integration / interface",
  supply: "Supply (hardware)"
} as const satisfies LabelMap;

export const commercialLabels = {
  allowance: "Allowance",
  by_others: "By others",
  excluded: "Excluded",
  included: "Included",
  install_note: "Install note",
  priced: "Priced supply",
  ve_candidate: "VE candidate"
} as const satisfies LabelMap;

export const requirementTypeLabels = {
  commissioning: "Commissioning / handover",
  commercial: "Commercial / VE",
  functional: "Functional",
  interface: "Interface / integration",
  performance: "Performance / standard",
  quantity: "Quantity / count"
} as const satisfies LabelMap;

export const sourceKindLabels = {
  benchmark: "Benchmark",
  catalog_workbook: "Catalog workbook",
  drawing: "Drawing",
  email: "Email",
  image: "Image",
  inferred: "AI-inferred",
  manual_entry: "Manual entry",
  narrative: "Tender narrative",
  note: "Note",
  other: "Other",
  pdf: "PDF",
  requirements_doc: "Requirements doc",
  rich_decision: "Authored decision",
  schedule: "Schedule / matrix"
} as const satisfies LabelMap;

export const priorityLabels = {
  blocking: "Must",
  default: "Standard",
  option: "Option",
  rfi: "Needs answer",
  ve: "VE"
} as const satisfies LabelMap;

export const confidenceLabels = {
  high: "High confidence",
  medium: "Medium confidence",
  low: "Low confidence",
  high_confidence: "High confidence",
  medium_confidence: "Medium confidence",
  low_confidence: "Low confidence"
} as const satisfies LabelMap;

export const requirementStatusLabels = {
  candidate: "Candidate",
  approved: "Approved",
  rejected: "Rejected",
  superseded: "Superseded"
} as const satisfies LabelMap;

export const statusLabels = {
  ...riskStatusLabels,
  ...commercialLabels,
  ...requirementStatusLabels
} as const satisfies LabelMap;

export const facetValueLabels = {
  system: systemLabels,
  severity: severityLabels,
  nature: riskNatureLabels,
  riskNature: riskNatureLabels,
  owner: ownerLabels,
  status: statusLabels,
  riskStatus: riskStatusLabels,
  workType: scopeWorkTypeLabels,
  commercial: commercialLabels,
  scopeStatus: commercialLabels,
  type: requirementTypeLabels,
  requirementType: requirementTypeLabels,
  sourceKind: sourceKindLabels,
  priority: priorityLabels,
  confidence: confidenceLabels
} as const satisfies Record<string, LabelMap>;

export const facetOrderByItemType = {
  risks: ["severity", "system", "nature", "owner", "status"],
  scope: ["scopeStatus", "system", "workType", "commercial"],
  requirements: ["system", "type", "priority", "confidence", "sourceKind", "status"]
} as const satisfies Record<FacetItemType, readonly string[]>;

export const groupByOptionsByItemType = {
  risks: [
    { value: null, label: "All risks" },
    { value: "severity", label: "Severity" },
    { value: "system", label: "System" },
    { value: "nature", label: "Nature" },
    { value: "owner", label: "Owner" }
  ],
  scope: [
    { value: null, label: "All scope" },
    { value: "scopeStatus", label: "Status" },
    { value: "system", label: "System" },
    { value: "workType", label: "Work type" },
    { value: "commercial", label: "Commercial" }
  ],
  requirements: [
    { value: null, label: "All requirements" },
    { value: "system", label: "System" },
    { value: "type", label: "Type" },
    { value: "priority", label: "Priority" },
    { value: "confidence", label: "Confidence" },
    { value: "sourceKind", label: "Source" },
    { value: "status", label: "Status" }
  ]
} as const satisfies Record<
  FacetItemType,
  readonly { value: string | null; label: string }[]
>;

export const riskFacetOrder = facetOrderByItemType.risks;
export const scopeFacetOrder = facetOrderByItemType.scope;
export const requirementFacetOrder = facetOrderByItemType.requirements;
export const riskGroupByOptions = groupByOptionsByItemType.risks;
export const scopeGroupByOptions = groupByOptionsByItemType.scope;
export const requirementGroupByOptions = groupByOptionsByItemType.requirements;

export const facetToneByFacetValue = {
  system: {
    heating_cooling: "neutral",
    underfloor_heating: "neutral",
    whole_home_ui: "neutral",
    metering: "neutral",
    bms_integration: "neutral",
    network_infra: "neutral",
    commissioning: "neutral"
  },
  severity: {
    blocker: "severity-blocker",
    warning: "severity-warning",
    info: "severity-info"
  },
  status: {
    included: "status-included",
    by_others: "status-by_others",
    excluded: "status-excluded",
    approved: "status-included",
    rejected: "status-excluded",
    candidate: "severity-warning",
    open: "severity-warning",
    resolved: "status-included",
    accepted: "status-included",
    converted_to_rfi: "severity-info",
    superseded: "neutral"
  },
  commercial: {
    allowance: "severity-warning",
    by_others: "status-by_others",
    excluded: "status-excluded",
    included: "status-included",
    install_note: "neutral",
    priced: "status-included",
    ve_candidate: "severity-info"
  },
  scopeStatus: {
    included: "status-included",
    by_others: "status-by_others",
    excluded: "status-excluded"
  },
  priority: {
    blocking: "severity-blocker",
    default: "neutral",
    option: "severity-info",
    rfi: "severity-warning",
    ve: "severity-info"
  },
  confidence: {
    high: "status-included",
    medium: "severity-warning",
    low: "severity-blocker",
    high_confidence: "status-included",
    medium_confidence: "severity-warning",
    low_confidence: "severity-blocker"
  }
} as const satisfies Record<string, Record<string, FacetTone>>;

const facetValueLabelLookup: Record<string, LabelMap> = facetValueLabels;
const facetToneLookup: Record<string, Record<string, FacetTone>> =
  facetToneByFacetValue;

const humaniseFallback = (value: string): string =>
  value
    .replaceAll("_", " ")
    .replaceAll("-", " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());

export function labelForFacetValue(facetKey: string, value: string): string {
  return facetValueLabelLookup[facetKey]?.[value] ?? humaniseFallback(value);
}

export function toneForFacetValue(
  facetKey: string,
  value: string
): FacetTone | undefined {
  return facetToneLookup[facetKey]?.[value];
}
